import { SENDER_TYPE } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  LubavSessionType,
  OFFICE_HOURS_SESSION_IDS,
  OFFICE_HOURS_SUBSESSION_IDS,
  LUBAV_SUPERSESSIONS,
  DOCUMENT_TYPES,
  DocumentType,
  PROFILE_ITEM_TYPES,
  ProfileItemType,
  OfficeHoursMessage,
} from '@backrs_ui/grow-ui/src/models/models';

import { PromptOption, PromptVariant } from './PromptButtons';

const majorsPromptOptions: readonly PromptOption[] = [
  {
    id: 'no-clue',
    badgeName: 'Explore',
    title:
      'I have no clue what career to pursue right now. Help me create a "short list" of majors that might be a good fit.',
  },
  {
    id: 'confident-direction',
    badgeName: 'Ask',
    title:
      "I'm confident in direction of my career (e.g., business, engineering, law, education) and I need help picking a major to get there",
  },
  {
    id: 'double-major',
    badgeName: 'Explore',
    title: 'Consider a double major',
  },
  {
    id: 'compare-options',
    badgeName: 'Learn',
    title: "Compare a few options I'm considering",
  },
  {
    id: 'switching-majors',
    badgeName: 'Ask',
    title: "I'm considering switching majors. Help me decide.",
  },
];

const officeHoursHighSchoolPromptOptions: readonly PromptOption[] = [
  {
    id: 'grades',
    title: 'Grades',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.HIGH_SCHOOL_GRADES,
    hiddenPrompt: 'Grades',
  },
  {
    id: 'act-sat',
    title: 'ACT/SAT',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.HIGH_SCHOOL_ACT_SAT,
    hiddenPrompt: 'ACT/SAT',
  },
  {
    id: 'college-majors',
    title: 'Majors',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.HIGH_SCHOOL_COLLEGE_MAJORS,
    hiddenPrompt: 'Majors',
  },
  {
    id: 'universities',
    title: 'Universities',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.HIGH_SCHOOL_UNIVERSITIES,
    hiddenPrompt: 'Universities',
  },
  {
    id: 'resume',
    title: 'Resume',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.HIGH_SCHOOL_RESUME,
    hiddenPrompt: 'Resume',
  },
  {
    id: 'careers',
    title: 'Careers',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.HIGH_SCHOOL_CAREERS,
    hiddenPrompt: 'Careers',
  },
  {
    id: 'scholarship',
    title: 'Scholarship',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.HIGH_SCHOOL_SCHOLARSHIP,
    hiddenPrompt: 'Scholarship',
  },
];

const officeHoursGuestPromptOptions: readonly PromptOption[] = [
  {
    id: 'grades',
    title: 'Grades',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.GUEST_GRADES,
    hiddenPrompt: 'Grades',
  },
  {
    id: 'careers',
    title: 'Careers',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.GUEST_CAREERS,
    hiddenPrompt: 'Careers',
  },
  {
    id: 'internships',
    title: 'Internships',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.GUEST_INTERNSHIPS,
    hiddenPrompt: 'Internships',
  },
  {
    id: 'resume',
    title: 'Resume',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.GUEST_RESUME,
    hiddenPrompt: 'Resume',
  },
  {
    id: 'majors',
    title: 'Majors',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.GUEST_MAJORS,
    hiddenPrompt: 'Majors',
  },
  {
    id: 'colleges',
    title: 'Colleges',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.GUEST_COLLEGES,
    hiddenPrompt: 'Colleges',
  },
  {
    id: 'scholarship',
    title: 'Scholarship',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.GUEST_SCHOLARSHIP,
    hiddenPrompt: 'Scholarship',
  },
];

const officeHoursCollegePromptOptions: readonly PromptOption[] = [
  {
    id: 'grades',
    title: 'Grades',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.COLLEGE_GRADES,
    hiddenPrompt: 'Grades',
  },
  {
    id: 'careers',
    title: 'Careers',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.COLLEGE_CAREERS,
    hiddenPrompt: 'Careers',
  },
  {
    id: 'internships',
    title: 'Internships',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.COLLEGE_INTERNSHIPS,
    hiddenPrompt: 'Internships',
  },
  {
    id: 'resume',
    title: 'Resume',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.COLLEGE_RESUME,
    hiddenPrompt: 'Resume',
  },
  {
    id: 'college-majors',
    title: 'Majors',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.COLLEGE_MAJORS,
    hiddenPrompt: 'Majors',
  },
  {
    id: 'scholarship',
    title: 'Scholarship',
    sessionId: OFFICE_HOURS_SUBSESSION_IDS.COLLEGE_SCHOLARSHIP,
    hiddenPrompt: 'Scholarship',
  },
];

const COACHING_CATEGORIES = appStrings.modules.supercoach.categories;

export const COACHING_LEAF_DOC_REQUIREMENTS: Record<
  string,
  {
    requireDocument?: DocumentType[];
    allowSkipDocument?: boolean;
    includeProfileItems?: ProfileItemType[];
  }
> = {
  coaching_academic_profile: {
    requireDocument: [DOCUMENT_TYPES.TRANSCRIPT, DOCUMENT_TYPES.RESUME],
    allowSkipDocument: true,
  },
  coaching_academic_snapshot: {
    requireDocument: [DOCUMENT_TYPES.TRANSCRIPT],
    allowSkipDocument: true,
  },
  coaching_college_odds: {
    requireDocument: [DOCUMENT_TYPES.TRANSCRIPT, DOCUMENT_TYPES.RESUME],
    allowSkipDocument: true,
  },
  coaching_jobs_odds: {
    requireDocument: [DOCUMENT_TYPES.RESUME],
    allowSkipDocument: true,
  },
  coaching_strengthen_profile: {
    requireDocument: [DOCUMENT_TYPES.RESUME],
    allowSkipDocument: true,
  },
  coaching_resume_optimizer: {
    requireDocument: [DOCUMENT_TYPES.RESUME],
    allowSkipDocument: false,
  },
  coaching_letter_of_rec: {
    requireDocument: [DOCUMENT_TYPES.TRANSCRIPT, DOCUMENT_TYPES.RESUME],
    allowSkipDocument: true,
  },
  coaching_study_skills: {
    requireDocument: [DOCUMENT_TYPES.TRANSCRIPT],
    allowSkipDocument: true,
  },
  coaching_the_interpreter: {
    requireDocument: [DOCUMENT_TYPES.PERSONAL_ASSESSMENT],
    allowSkipDocument: false,
  },
  coaching_are_you_on_track: {
    includeProfileItems: [PROFILE_ITEM_TYPES.TRACK_ASSESSMENT],
  },
  coaching_college_shortlist: {
    includeProfileItems: [PROFILE_ITEM_TYPES.COLLEGE_SHORTLIST],
  },
  coaching_majors: {
    includeProfileItems: [PROFILE_ITEM_TYPES.MAJORS],
  },
};

const supercoachCategoryOptions: readonly PromptOption[] = [
  {
    id: 'coaching-cat-student-snapshot',
    title: COACHING_CATEGORIES.studentSnapshot,
    promptButtonId: 'coaching_category_student_snapshot',
  },
  {
    id: 'coaching-cat-coaching-sessions',
    title: COACHING_CATEGORIES.coachingSessions,
    promptButtonId: 'coaching_category_coaching_sessions',
  },
  {
    id: 'coaching-cat-deep-dives',
    title: COACHING_CATEGORIES.deepDives,
    promptButtonId: 'coaching_category_deep_dives',
  },
  {
    id: 'coaching-cat-email-drafting',
    title: COACHING_CATEGORIES.emailDrafting,
    promptButtonId: 'coaching_category_email_drafting',
  },
];

export const getOfficeHoursPromptOptions = (
  sessionType: LubavSessionType,
  isHighSchool?: boolean,
  isGuest?: boolean,
): readonly PromptOption[] | undefined => {
  if (sessionType === LUBAV_SUPERSESSIONS.SUPERCOACH) {
    return supercoachCategoryOptions;
  }
  if (sessionType === OFFICE_HOURS_SESSION_IDS.MAJORS) {
    return majorsPromptOptions;
  }
  if (sessionType === LUBAV_SUPERSESSIONS.OFFICE_HOURS) {
    if (isGuest) {
      return officeHoursGuestPromptOptions;
    }

    return isHighSchool === true
      ? officeHoursHighSchoolPromptOptions
      : officeHoursCollegePromptOptions;
  }

  return undefined;
};

export const shouldShowPromptsInMessage = ({
  activeSession,
  currentMessage,
  currentMessageIndex,
  officeHoursMessages,
  isTyping,
  isStreaming,
}: {
  activeSession: LubavSessionType | null;
  currentMessage: OfficeHoursMessage;
  currentMessageIndex: number;
  officeHoursMessages: OfficeHoursMessage[];
  isTyping: boolean;
  isStreaming: boolean;
}): boolean => {
  if (!activeSession) {
    return false;
  }

  if (
    activeSession !== LUBAV_SUPERSESSIONS.OFFICE_HOURS &&
    activeSession !== LUBAV_SUPERSESSIONS.SUPERCOACH
  ) {
    return false;
  }

  if (currentMessage.sender !== SENDER_TYPE.AI) {
    return false;
  }

  if (currentMessageIndex !== officeHoursMessages.findIndex((m) => m.sender === SENDER_TYPE.AI)) {
    return false;
  }

  if (officeHoursMessages.length === 1) {
    if (isStreaming || isTyping) {
      return false;
    }
  }

  return !currentMessage.componentType;
};

export const getPromptVariant = (activeSession: LubavSessionType | null): PromptVariant => {
  return activeSession === OFFICE_HOURS_SESSION_IDS.MAJORS ? 'BADGE' : 'SINGLE_SELECT';
};
